import React, { useState, useRef } from 'react';
import { Send, Paperclip, Smile, Bot, User } from 'lucide-react';
import { useChatContext } from '../hooks/useChatContext';

const MessageInput: React.FC = () => {
  const { state, sendMessage, takeoverFromAI, resumeAI } = useChatContext();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !state.activeConversation || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await sendMessage(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const handleTakeoverFromAI = async () => {
    if (!state.activeConversation) return;
    await takeoverFromAI(state.activeConversation.id, 'Agent manual takeover');
  };

  const handleResumeAI = async () => {
    if (!state.activeConversation) return;
    await resumeAI(state.activeConversation.id);
  };

  if (!state.activeConversation) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* AI Control Bar */}
      {state.activeConversation && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {state.activeConversation.status === 'ai-handled' ? (
                <>
                  <Bot className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-600 font-medium">AI is handling this conversation</span>
                </>
              ) : (
                <>
                  <User className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">You are handling this conversation</span>
                </>
              )}
            </div>
            
            <div className="flex gap-2">
              {state.activeConversation.status === 'ai-handled' ? (
                <button
                  onClick={handleTakeoverFromAI}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Take Over
                </button>
              ) : (
                <button
                  onClick={handleResumeAI}
                  className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Hand to AI
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSubmit} className="p-4">
        <div className="flex items-end gap-3">
          {/* Attachment Button */}
          <button
            type="button"
            className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            title="Attach file"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              disabled={isSubmitting}
            />
            
            {/* Emoji Button */}
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              title="Add emoji"
            >
              <Smile className="w-5 h-5" />
            </button>
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!message.trim() || isSubmitting}
            className="flex-shrink-0 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            title="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Replies */}
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => setMessage('Thank you for contacting us. How can I help you today?')}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            Greeting
          </button>
          <button
            type="button"
            onClick={() => setMessage('I understand your concern. Let me help you with that.')}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            Understanding
          </button>
          <button
            type="button"
            onClick={() => setMessage('Is there anything else I can help you with?')}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            Follow-up
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageInput;
